import { formatCurrency } from "@gymflow/helpers";
import {
  MembershipSummaryDTO,
  ProductOrderSummaryDTO,
  SessionPackSummaryDTO,
} from "@gymflow/types";
import { FormikContextType, FormikProvider } from "formik";
import { SetStateAction } from "react";
import { useTranslation } from "react-i18next";

import { useClubSettings } from "../../providers";
import { ShopSidebarFormValues } from "../Shop/ShopSidebar/ShopSidebarProvider";
import { CheckoutFormValues } from "../site/HostedPagesCheckout";
import {
  AlertCircleIcon,
  CheckCircleBrokenIcon,
  ErrorMessage,
  FormInput,
  Input,
} from ".";
import { Tag01Icon } from "./icons/Tag01Icon";
import { Wallet02Icon } from "./icons/Wallet02Icon";

type WalletAndPromoCodeBlockProps = {
  promotionCodeInput: string | undefined;
  setPromotionCode: (value: SetStateAction<string | undefined>) => void;
  setPromotionCodeInput: (value: SetStateAction<string>) => void;
  promotionCode: string | undefined;
  summary:
    | MembershipSummaryDTO
    | SessionPackSummaryDTO
    | ProductOrderSummaryDTO
    | undefined;
  isFetchingSummary: boolean;
  formik: FormikContextType<ShopSidebarFormValues | CheckoutFormValues>;
  walletBalance: number;
};

// for future: need to remove setState logic (need to use form logic)
export const WalletAndPromoCodeBlock = ({
  promotionCodeInput,
  promotionCode,
  summary,
  isFetchingSummary,
  walletBalance,
  formik,
  setPromotionCode,
  setPromotionCodeInput,
}: WalletAndPromoCodeBlockProps) => {
  const {
    club: { defaultCurrency },
  } = useClubSettings();
  const { t } = useTranslation();

  return (
    <div className="bg-gray-0 shadow-xs flex flex-col gap-4 rounded-2xl border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-950">
      <div className="flex flex-col justify-between gap-1.5 lg:flex-row">
        <div className="flex flex-row items-center gap-2 self-start">
          <div className="flex size-9 items-center justify-center rounded-full bg-gray-100">
            <Tag01Icon />
          </div>
          <div className="dark:text-gray-25 text-sm font-medium text-gray-950">
            {t("components.walletAndPromoCodeBlock.promotionCode.label")}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 lg:w-60">
          <Input
            value={promotionCodeInput || ""}
            onChange={(e) => {
              setPromotionCodeInput(e.target.value);
            }}
            disabled={promotionCode !== undefined}
            placeholder={t(
              "components.walletAndPromoCodeBlock.promotionCode.placeholder",
            )}
            endIcon={
              summary &&
              promotionCode &&
              summary?.validPromotionCode &&
              (summary.validPromotionCode === "YES" ? (
                <CheckCircleBrokenIcon
                  className="size-5"
                  pathClassName="stroke-success-500"
                />
              ) : (
                <AlertCircleIcon className="size-5" />
              ))
            }
            button={{
              label:
                promotionCode === undefined
                  ? t("portal.common.apply")
                  : t("portal.common.remove"),
              loading: isFetchingSummary,
              onClick: () => {
                if (!promotionCode && promotionCodeInput) {
                  setPromotionCode(promotionCodeInput);
                } else {
                  setPromotionCode(undefined);
                  setPromotionCodeInput("");
                }
                if (promotionCodeInput || promotionCode) {
                  formik.setFieldValue("walletAmount", "");
                }
              },
            }}
          />
          {summary &&
            promotionCode &&
            summary?.validPromotionCode !== "YES" && (
              <ErrorMessage
                text={t(
                  "components.walletAndPromoCodeBlock.promotionCode.invalid",
                )}
              />
            )}
        </div>
      </div>
      <div className="h-px w-full bg-gray-300 dark:bg-gray-700" />
      <div className="flex flex-col justify-between gap-1.5 lg:flex-row">
        <div className="flex flex-row items-start gap-2">
          <div className="flex size-9 items-center justify-center rounded-full bg-gray-100">
            <Wallet02Icon />
          </div>
          <div className="flex flex-col">
            <div className="flex flex-row items-center gap-1.5">
              <div className="dark:text-gray-25 text-base font-medium text-gray-950">
                {t("components.walletAndPromoCodeBlock.walletBalance.label")}
              </div>
              <div className="border-secondary-200 dark:border-secondary-800 dark:bg-secondary-950 bg-secondary-25 rounded-full border px-1.5 py-0.5">
                <div className="text-secondary-700 dark:text-secondary-200 text-xs font-medium">
                  {formatCurrency(walletBalance, defaultCurrency)}
                </div>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t("components.walletAndPromoCodeBlock.walletBalance.subLabel")}
            </div>
          </div>
        </div>
        <div className="lg:w-60">
          <FormikProvider value={formik}>
            <FormInput
              name="walletAmount"
              type="number"
              placeholder={t(
                "components.walletAndPromoCodeBlock.walletBalance.placeholder",
              )}
              endIcon={
                !!formik.values.walletAmount && (
                  <CheckCircleBrokenIcon
                    className="size-5"
                    pathClassName="stroke-success-500"
                  />
                )
              }
            />
          </FormikProvider>
        </div>
      </div>
    </div>
  );
};
